/**
 * ErrorDescriptionPlugin
 *
 * Vue plugin that provides:
 *
 * 1. `this.$showError(code, options)` — inside Vue components
 * 2. `errorDescription.show(code, options)` — standalone, for use in plain JS
 *    modules like errorHandlerRest (same pattern as mcToast)
 *
 * Installation:
 *   import ErrorDescriptionPlugin from '@/plugins/error-description-plugin'
 *   Vue.use(ErrorDescriptionPlugin)
 *
 * Usage in Vue components:
 *   this.$showError('ERR_001')
 *   this.$showError('ERR_001', { onAction: () => { ... } })
 *
 * Usage in plain JS (e.g. errorHandlerRest.js):
 *   import { errorDescription } from '@/plugins/error-description-plugin'
 *   errorDescription.show('ERR_001')
 *   errorDescription.show('ERR_001', { buttonText: 'OK', onAction: () => {} })
 */
import Vue from 'vue'
import ErrorDescriptionModal from '@/components/ErrorDescriptionModal.vue'

// ============================================
// Shared reactive state (singleton)
// ============================================
const state = Vue.observable({
  visible: false,
  errorCode: '',
  overrides: {},
  persistent: false,
  onAction: null,
  onClose: null,
})

/**
 * Core show function — shared between plugin and standalone export
 * @param {String|null} code - The error code to fetch, or null for pure override mode
 * @param {Object} options - Override options and callbacks
 */
function _showError(code, options = {}) {
  const { onAction, onClose, persistent, ...overrides } = options

  state.errorCode = code || ''
  state.overrides = overrides
  state.persistent = persistent || false
  state.onAction = onAction || null
  state.onClose = onClose || null
  state.visible = true
}

/**
 * Core hide function
 */
function _hideError() {
  state.visible = false
}

// ============================================
// Standalone export (like mcToast)
// ============================================
// Usage: import { errorDescription } from '@/plugins/error-description-plugin'
//        errorDescription.show('ERR_001')
//        errorDescription.show('ERR_001', { onAction: () => {} })
// ============================================
export const errorDescription = {
  /**
   * Show an error description modal by error code
   * @param {String|null} code - Error code to fetch from the API
   * @param {Object} [options] - Optional overrides and callbacks
   * @param {String} [options.header] - Override header text
   * @param {String} [options.subHeader] - Override sub header text
   * @param {String} [options.body] - Override body HTML
   * @param {String} [options.footer] - Override footer text
   * @param {String} [options.type] - Override type: 'success' | 'warn' | 'error' | 'info'
   * @param {String} [options.buttonText] - Override button text
   * @param {Boolean} [options.persistent] - If true, clicking backdrop won't close
   * @param {Function} [options.onAction] - Callback when action button clicked
   * @param {Function} [options.onClose] - Callback when modal closed
   */
  show: _showError,

  /**
   * Programmatically close the error description modal
   */
  hide: _hideError,
}

// ============================================
// Vue Plugin
// ============================================
const ErrorDescriptionPlugin = {
  install(VueInstance) {
    // Expose on Vue prototype for component usage
    VueInstance.prototype.$showError = _showError
    VueInstance.prototype.$hideError = _hideError
    VueInstance.prototype.$errorDescriptionState = state

    /**
     * Global component — mount once in root App.vue or main layout:
     *
     * <template>
     *   <div id="app">
     *     <router-view />
     *     <ErrorDescriptionModalGlobal />
     *   </div>
     * </template>
     */
    VueInstance.component('ErrorDescriptionModalGlobal', {
      render(h) {
        return h(ErrorDescriptionModal, {
          props: {
            visible: state.visible,
            errorCode: state.errorCode,
            overrides: state.overrides,
            persistent: state.persistent,
          },
          on: {
            'update:visible': (val) => {
              state.visible = val
              if (!val && typeof state.onClose === 'function') {
                state.onClose()
              }
            },
            action: (data) => {
              if (typeof state.onAction === 'function') {
                state.onAction(data)
              }
            },
            close: () => {
              if (typeof state.onClose === 'function') {
                state.onClose()
              }
            },
          },
        })
      },
    })
  },
}

export default ErrorDescriptionPlugin
